import { useState } from "react";
import { useSelector } from "react-redux";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper.ts";
import InlineTitleEditor from "@entrilia/shared/components/InlineTitleEditor.tsx";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext.ts";
import type { BiApiClient } from "../../../../contexts/ApiClientProviderContext";
import { useBiApiClientProvider } from "../../../../contexts/BiApiClientProviderContext.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch";
import {
  selectCurrentReportClientCode,
  selectCurrentReportId,
  selectCurrentReportName,
} from "../../../../store/selectors/currentReportSelectors";
import { updateReport } from "../../../../store/thunks/currentReportThunks";
import ChangeReportNameButton from "./ChangeReportNameButton.tsx";

interface Props {
  showEditReportName: boolean;
  onRenamingChanged: (isRenaming: boolean) => void;
  readonlyMode?: boolean;
}

const ReportNameChanger = ({ onRenamingChanged, showEditReportName, readonlyMode }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const { validateName, renameReport } = useBiApiClientProvider<BiApiClient>();

  const reportId = useSelector(selectCurrentReportId);
  const clientCode = useSelector(selectCurrentReportClientCode);
  const reportName = useSelector(selectCurrentReportName);
  const dispatch = useAppDispatch();

  const [isRenaming, setIsRenaming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportNameIsNotValidError, setReportNameIsNotValidError] = useState<string>();

  const isValidReportName = async (name: string) => {
    const [result, error] = await withErrorHandling(validateName)({
      reportId,
      name,
      clientCode: clientCode ?? "",
    });

    if (error) {
      sendNotificationError("An error occurred while renaming the report.");
      return false;
    }
    if (result.nameExists) {
      setReportNameIsNotValidError("A report with this name already exists");
      return false;
    }
    setReportNameIsNotValidError(undefined);
    return true;
  };

  const changeReportName = async (name: string) => {
    const [result, error] = await withErrorHandling(renameReport)({
      reportId: reportId ?? "",
      name,
      clientCode: clientCode ?? "",
    });

    if (error) {
      sendNotificationError("An error occurred while renaming the report.");
    }
    return result;
  };

  const handleRename = async (name: string) => {
    setLoading(true);

    if (await isValidReportName(name)) {
      setReportNameIsNotValidError(undefined);
      const newReport = await changeReportName(name);

      if (newReport) {
        dispatch(updateReport({ name: newReport.name }));
        changeRenamingState(false);
      }
    }
    setLoading(false);
  };

  const changeRenamingState = (isRenaming: boolean) => {
    setIsRenaming(isRenaming);
    onRenamingChanged(isRenaming);
  };

  const handleReportNameChanged = (newReportName: string) => {
    if (newReportName.trim().length < 2) {
      setReportNameIsNotValidError("Name should be at least 2 characters long.");
    } else {
      setReportNameIsNotValidError(undefined);
    }
  };

  if (!isRenaming) {
    return (
      <ChangeReportNameButton
        onClick={() => changeRenamingState(true)}
        hidden={!showEditReportName}
        disabled={readonlyMode}
      />
    );
  }

  return (
    <InlineTitleEditor
      title={reportName}
      onApply={handleRename}
      onClose={() => {
        changeRenamingState(false);
        setReportNameIsNotValidError(undefined);
      }}
      loading={loading}
      validationError={reportNameIsNotValidError}
      onTitleChanged={handleReportNameChanged}
      placeholder="Enter Report Name"
    />
  );
};

export default ReportNameChanger;
